import { useRef, useState, type FormEvent } from 'react';
import { Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { accountApi, type PasswordChange } from '@/api/account';
import { backendCode } from '@/api/errors';
import { useAuth } from '@/auth/authHooks';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { SubmitButton } from '@/components/SubmitButton';
import { useActionError, type ActionError } from '@/components/useActionError';
import { useFieldErrors } from '@/components/useFieldErrors';
import { SectionGuard } from './SectionGuard';
import { useReportDirty } from './useReportDirty';
import { FIELD_WIDTH } from '@/theme';

const MIN_LENGTH = 8;
const MAX_LENGTH = 100;
const CODE_PATTERN = /^\d{6}$/;

const FIELDS = ['current', 'next', 'code'] as const;

/** saas-auth codes this form answers with its own copy, next to the field they name. */
const FORM_ERRORS: Record<string, { field: (typeof FIELDS)[number]; key: string }> = {
  INVALID_CURRENT_PASSWORD: { field: 'current', key: 'account.password.errorCurrent' },
  INVALID_MFA_CODE: { field: 'code', key: 'account.password.errorCode' },
};

/**
 * `POST /password/challenge` (emails a 6-digit code), then `POST /password
 * { currentPassword?, newPassword, mfaCode }`. `currentPassword` is asked only when the member
 * has one — a Google-only member sets a first password without it. Success signs the member out
 * EVERYWHERE (saas-auth revokes every session), so the console clears its session and lands on
 * the signed-out page with "Password changed — sign in again". That navigation goes to `/auth/`,
 * which the unsaved-changes guard never blocks.
 */
export function PasswordSection({
  hasPassword,
  onDirtyChange,
}: {
  hasPassword: boolean;
  /** The page owns the one unsaved-changes guard (one blocker per page): report the edited state. */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const describe = useActionError();
  const currentRef = useRef<HTMLInputElement>(null);
  const nextRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const fields = useFieldErrors(
    FIELDS,
    { current: currentRef, next: nextRef, code: codeRef },
    busy,
  );
  // Edited: any of the fields holds something (FRM-008).
  const dirty = current !== '' || next !== '' || code !== '';
  useReportDirty(dirty, onDirtyChange);

  async function sendCode() {
    setSending(true);
    setError(null);
    try {
      await accountApi.passwordChallenge();
      setCodeSent(true);
    } catch (err) {
      setError(describe(err));
    } finally {
      setSending(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const found: Partial<Record<(typeof FIELDS)[number], string>> = {};
    if (hasPassword && current.length === 0) found.current = t('validation.passwordCurrent');
    if (next.length < MIN_LENGTH) found.next = t('validation.passwordNew', { min: MIN_LENGTH });
    if (!CODE_PATTERN.test(code.trim())) found.code = t('validation.passwordCode');
    if (Object.keys(found).length > 0) {
      setError(null);
      fields.fail(found);
      return;
    }
    setBusy(true);
    setError(null);
    fields.reset();
    const change: PasswordChange = { newPassword: next, mfaCode: code.trim() };
    if (hasPassword) change.currentPassword = current;
    try {
      await accountApi.changePassword(change);
    } catch (err) {
      const action = describe(err);
      const formError = FORM_ERRORS[backendCode(err) ?? ''];
      if (formError) fields.fail({ [formError.field]: t(formError.key) });
      else setError(action);
      setBusy(false);
      return;
    }
    signOut();
    navigate('/auth/signed-out', { replace: true, state: { passwordChanged: true } });
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h2" component="h2" sx={{ mb: 0.5 }}>
        {t('account.password.title')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {hasPassword ? t('account.password.lead') : t('account.password.leadFirst')}
      </Typography>
      <Stack
        component="form"
        noValidate
        spacing={2}
        onSubmit={(e) => void submit(e)}
        sx={{ maxWidth: FIELD_WIDTH.lg }}
      >
        <ActionErrorAlert error={error} />
        {hasPassword ? (
          <TextField
            type="password"
            label={t('account.password.current')}
            value={current}
            onChange={(e) => {
              setCurrent(e.target.value);
              fields.clear('current');
            }}
            inputRef={currentRef}
            error={fields.errors.current !== undefined}
            helperText={fields.errors.current}
            autoComplete="current-password"
            slotProps={{ htmlInput: { maxLength: MAX_LENGTH } }}
            required
          />
        ) : null}
        <TextField
          type="password"
          label={t('account.password.new')}
          value={next}
          onChange={(e) => {
            setNext(e.target.value);
            fields.clear('next');
          }}
          inputRef={nextRef}
          error={fields.errors.next !== undefined}
          helperText={fields.errors.next ?? t('account.password.newHelp', { min: MIN_LENGTH })}
          autoComplete="new-password"
          slotProps={{ htmlInput: { maxLength: MAX_LENGTH } }}
          required
        />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => void sendCode()}
            disabled={busy || sending}
          >
            {codeSent ? t('account.password.resendCode') : t('account.password.sendCode')}
          </Button>
          {codeSent ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('account.password.codeSent')}
            </Typography>
          ) : null}
        </Stack>
        <TextField
          label={t('account.password.code')}
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            fields.clear('code');
          }}
          inputRef={codeRef}
          error={fields.errors.code !== undefined}
          helperText={fields.errors.code}
          autoComplete="one-time-code"
          slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6 } }}
          required
        />
        <Stack direction="row">
          <SubmitButton type="submit" loading={busy}>
            {t('account.password.submit')}
          </SubmitButton>
        </Stack>
      </Stack>
      {onDirtyChange === undefined ? (
        <SectionGuard when={dirty} name={t('unsaved.names.password')} />
      ) : null}
    </Paper>
  );
}
